import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { universityCatalog, universityDbMeta } from '../data/universityCatalog';
import { AnalyticsEvents, trackEvent } from '../utils/analytics';
import { usePageMeta } from '../hooks/usePageMeta';
import '../styles/Resources.css';
import '../styles/Universities.css';

const FIELD_FILTERS = [
  { id: 'all', label: '전체' },
  { id: 'stem', label: '理工·情報（이공·정보）' },
  { id: 'medicine', label: '医歯薬·保健（의치약·보건）' },
  { id: 'humanities', label: '文系·社会（문과·사회）' },
];

const TYPE_FILTERS = [
  { id: 'all', label: '전체 유형' },
  { id: 'national', label: '国立（국립）' },
  { id: 'public', label: '公立（공립）' },
  { id: 'private', label: '私立（사립）' },
];

/** 내부 tag id → 日本語（한국어） 표시 */
const TAG_LABELS = {
  national: '国立（국립）',
  public: '公立（공립）',
  private: '私立（사립）',
  stem: '理工·情報（이공·정보）',
  medicine: '医歯薬·保健（의치약·보건）',
  humanities: '文系·社会（문과·사회）',
};

function tagLabel(tag) {
  return TAG_LABELS[tag] || tag;
}

function siteHost(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

const Universities = () => {
  const [field, setField] = useState('all');
  const [type, setType] = useState('all');

  usePageMeta({
    title: '대학 입시 안내',
    description:
      '비영리 입시 안내 목적으로 연결을 허용받은 일본 대학 공식 입시·모집요항 사이트 목록입니다. 합격선·정원 숫자는 올리지 않습니다.',
    path: '/universities',
  });

  const rows = useMemo(() => {
    return universityCatalog.filter((u) => {
      if (field !== 'all' && !u.tags?.includes(field)) return false;
      if (type !== 'all' && !u.tags?.includes(type)) return false;
      return true;
    });
  }, [field, type]);

  return (
    <div className="apple-page apple-universities">
      <header className="apple-page-hero">
        <p className="apple-eyebrow">Official admissions</p>
        <h1 className="apple-display">대학 입시 안내</h1>
        <p className="apple-lede">
          비영리·입시 안내 목적으로 대학 측에서 공식 사이트 연결을 허용한 목록입니다. 링크는 각 대학
          공식 입시·모집요항 페이지로만 이어집니다. 과목·일정·자격은 해당 연도 요강 원문으로 확인하세요.
          합격선·정원 숫자·요강 PDF 본문은 올리지 않습니다.
        </p>
        <p className="apple-univ-count">
          {rows.length}곳 · 전체 {universityCatalog.length}곳
          {universityDbMeta?.syncedAt ? ` · sync ${universityDbMeta.syncedAt.slice(0, 10)}` : ''}
        </p>
      </header>

      <div className="apple-resource-filters" role="tablist" aria-label="계열 필터">
        {FIELD_FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={field === f.id}
            className={field === f.id ? 'is-on' : ''}
            onClick={() => setField(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="apple-resource-filters apple-univ-type-filters" role="tablist" aria-label="유형 필터">
        {TYPE_FILTERS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={type === t.id}
            className={type === t.id ? 'is-on' : ''}
            onClick={() => setType(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="apple-resource-table-wrap">
        <table className="apple-resource-table">
          <thead>
            <tr>
              <th scope="col">대학</th>
              <th scope="col">유형·계열</th>
              <th scope="col">요강에서 볼 포인트</th>
              <th scope="col">확인</th>
              <th scope="col">링크</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td data-label="대학">
                  <span className="apple-univ-name">
                    {row.nameJa}
                    <span className="apple-univ-ko">（{row.nameKo}）</span>
                  </span>
                </td>
                <td data-label="유형·계열">
                  <span className="apple-univ-tags">
                    {(row.tags || []).map((tag) => (
                      <span key={tag} className="apple-univ-tag">
                        {tagLabel(tag)}
                      </span>
                    ))}
                  </span>
                </td>
                <td data-label="요강에서 볼 포인트">{row.notes}</td>
                <td data-label="확인">{row.verifiedAt}</td>
                <td data-label="링크">
                  <a
                    className="apple-univ-site-link"
                    href={row.admissionUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      trackEvent(AnalyticsEvents.RESOURCE_OUTBOUND, {
                        id: row.id,
                        org: row.nameJa,
                        source: 'universities',
                      })
                    }
                  >
                    공식 사이트
                  </a>
                  <span className="apple-univ-host">{siteHost(row.admissionUrl)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && (
        <p className="apple-resource-note">해당 필터에 맞는 대학이 없습니다. 필터를 바꿔 보세요.</p>
      )}

      <p className="apple-resource-note">
        {universityDbMeta?.policy ||
          '비영리 입시 안내. 공식 입시 사이트 연결만. 요강 PDF 본문 재게시·합격선 숫자 없음.'}{' '}
        지망 학부 요강·출원자격은 반드시 대학 공식 페이지에서 재확인하세요.
      </p>

      <div className="apple-page-foot">
        <Link to="/finder" className="apple-btn apple-btn-primary">
          루트 찾기
        </Link>
        <Link to="/resources" className="apple-link">
          공식 자료
        </Link>
        <Link to="/compare" className="apple-link">
          대학 비교
        </Link>
      </div>
    </div>
  );
};

export default Universities;
